import React, { useContext, useEffect, useMemo, useState } from "react";
import "./FluidMorphTabs.css";
import { TAB_ORDER } from "../config/tabOrder";
import { CartContext } from "../pages/CartPage/CartContext";
import {
  Boxes,
  GraduationCap,
  Hammer,
  Smartphone,
  UtensilsCrossed,
} from "lucide-react";

const normalize = (s) => (s || "").toLowerCase().replace(/\s+/g, "").replace(/[^a-z0-9]/g, "");

const TAB_META = {
  "3amigos": { icon: UtensilsCrossed, label: "3amigos" },
  "techbeginproducts": { icon: Boxes, label: "Techbegin" },
  "btechprojects": { icon: GraduationCap, label: "Projects" },
  "smartphone": { icon: Smartphone, label: "Mobiles" },
  "woodwork": { icon: Hammer, label: "Woodwork" },
};

const toNumberOrNull = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const getTabMeta = (title) => TAB_META[normalize(title)] || { icon: Boxes, label: title };

const FluidMorphTabs = ({ tabs = [], loading = false, navigate, foodMode = false }) => {
  const { addToCart } = useContext(CartContext);
  const allOrderedTabs = TAB_ORDER.map((name) => tabs.find((t) => normalize(t.title) === normalize(name))).filter(Boolean);
  
  // Filter tabs based on foodMode
  const orderedTabs = foodMode
    ? allOrderedTabs.filter((t) => normalize(t.title) === normalize('3amigos'))
    : allOrderedTabs.filter((t) => normalize(t.title) !== normalize('3amigos'));
  const [active, setActive] = useState(0);
  const [animatedButtonId, setAnimatedButtonId] = useState(null);
  const [flyingItem, setFlyingItem] = useState(null);

  useEffect(() => {
    setActive(0);
  }, [foodMode, orderedTabs.length]);

  useEffect(() => {
    if (active >= orderedTabs.length) {
      setActive(0);
    }
  }, [active, orderedTabs.length]);

  const activeTab = useMemo(() => orderedTabs[active] || null, [orderedTabs, active]);

  const handleAddToCart = (product, event) => {
    const productId = product._id || product.id;
    const cartProduct = {
      id: productId,
      name: product.name,
      price: Number(product.price || 0),
      image: product.image || product.imageUrl || "",
      description: product.description || "",
      categoryId: product.categoryId || product.category || '',
      checkinDT: !!product.checkinDT,
      multiDayDiscountDays: Number(product.multiDayDiscountDays || 0),
      multiDayDiscountPercent: Number(product.multiDayDiscountPercent || 0),
      quantity: 1,
    };
    addToCart(cartProduct);
    setAnimatedButtonId(productId);
    setTimeout(() => setAnimatedButtonId(null), 300);

    const buttonRect = event.currentTarget.getBoundingClientRect();
    const cartIcons = document.querySelectorAll('[data-cart-icon]');
    let cartIcon = null;
    cartIcons.forEach((icon) => {
      const rect = icon.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        cartIcon = icon;
      }
    });

    if (cartIcon) {
      const cartRect = cartIcon.getBoundingClientRect();
      const startX = buttonRect.left + buttonRect.width / 2;
      const startY = buttonRect.top + buttonRect.height / 2;
      const endX = cartRect.left + cartRect.width / 2;
      const endY = cartRect.top + cartRect.height / 2;
      const deltaX = endX - startX;
      const deltaY = endY - startY;
      setFlyingItem({ id: Date.now(), startX, startY, deltaX, deltaY, productName: product.name.split(" ")[0] });
      setTimeout(() => setFlyingItem(null), 800);
    }
  };

  const goToDetail = (product) => {
    if (typeof navigate === "function") navigate("productDetail", product._id || product.id);
  };

  return (
    <div className={`fluid-tabs container mx-auto px-4 mt-5 ${foodMode ? 'food-mode' : ''}`}>
      {loading ? (
        <div className="catalog-loading" aria-hidden="true">
          <div className="tabs-row">
            <div className="tab-btn skeleton-tab">Loading</div>
            <div className="tab-btn skeleton-tab">Loading</div>
            <div className="tab-btn skeleton-tab">Loading</div>
          </div>
          <div className="catalog-panel skeleton-panel" />
        </div>
      ) : (
        <>
          <div className="catalog-tabs-shell">
            <div className="tabs-row" role="tablist" aria-label="Product categories">
              {orderedTabs.map((t, i) => {
                const { icon: Icon } = getTabMeta(t.title);
                return (
                  <button
                    key={t.id}
                    role="tab"
                    aria-selected={active === i}
                    className={`tab-btn ${active === i ? "active" : ""}`}
                    onClick={() => setActive(i)}
                  >
                    <Icon className="tab-icon" size={18} strokeWidth={2.2} />
                    <span className="tab-label">{t.title}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {activeTab && (
            <div className="catalog-panel">
              <div className="cards-scroll-container">
                {activeTab.products && activeTab.products.map((product, index) => {
                const bgColors = ["from-blue-50 to-blue-100", "from-pink-50 to-pink-100", "from-teal-50 to-teal-100", "from-yellow-50 to-yellow-100"];
                const price = Number(product.price || 0);
                const mrp = Number(product.mrp || 0);
                const showMrp = mrp > 0 && mrp > price;

                const calories = toNumberOrNull(product.calories);
                const preparationTime = toNumberOrNull(product.preparationTime);
                const protein = toNumberOrNull(product.protein);
                const metaParts = [];
                if (preparationTime != null) metaParts.push(`${preparationTime}m`);
                if (calories != null) metaParts.push(`${calories}cal`);
                if (protein != null) metaParts.push(`${protein}gP`);
                const hasMeta = metaParts.length > 0;

                return (
                  <div key={product._id || product.id} className="card-item bg-white rounded-3xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col border border-slate-200/70">
                    <div
                      className="w-full h-44 bg-white flex items-center justify-center overflow-hidden rounded-t-3xl cursor-pointer relative"
                      onClick={() => goToDetail(product)}
                    >
                      {/* Discount badge removed as per request */}
                      {product.image || product.imageUrl ? (
                        <img
                          src={product.image || product.imageUrl}
                          alt={product.name}
                          className="w-full h-full object-cover rounded-2xl"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `https://placehold.co/400x300/E0F2F7/000000?text=${(product.name || "Product").replace(/\s/g, "+")}`;
                          }}
                        />
                      ) : (
                        <div className={`w-full h-full bg-gradient-to-br ${bgColors[index % bgColors.length]} flex items-center justify-center rounded-2xl`}>
                          <h3 className="text-2xl font-black text-gray-800 text-center">{(product.name || "").split(" ")[0]}</h3>
                        </div>
                      )}
                    </div>

                    <div className="relative px-4 pt-4 pb-4 flex flex-col flex-grow">
                      <h4 className="text-sm font-bold text-gray-800 mb-1 line-clamp-1 cursor-pointer" onClick={() => goToDetail(product)}>
                        {product.name}
                      </h4>
                      <p className="text-xs text-gray-500 mb-2 line-clamp-2">{product.description}</p>

                      {hasMeta && (
                        <div className="text-[11px] text-gray-700 mb-2 mt-1 whitespace-nowrap overflow-hidden text-ellipsis">
                          {metaParts.join(" • ")}
                        </div>
                      )}

                      <div className="mt-auto flex items-end justify-between gap-2">
                        <div className="flex items-end gap-2 min-w-0">
                          {showMrp && (
                            <span className="text-sm text-gray-500 line-through whitespace-nowrap">₹{mrp.toFixed(2)}</span>
                          )}
                          <span className={`text-lg font-bold whitespace-nowrap ${foodMode ? 'text-green-600' : 'text-slate-900'}`}>₹{price.toFixed(2)}</span>
                        </div>
                        <button
                          className={`add-to-cart-btn ${animatedButtonId === (product._id || product.id) ? "animate-bounce" : ""}`}
                          onClick={(e) => handleAddToCart(product, e)}
                          aria-label="Add to cart"
                          title="Add to cart"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {flyingItem && (
        <div
          className="flying-to-cart"
          style={{
            position: "fixed",
            left: `${flyingItem.startX}px`,
            top: `${flyingItem.startY}px`,
            transform: `translate(${flyingItem.deltaX}px, ${flyingItem.deltaY}px)`,
            zIndex: 9999,
            pointerEvents: "none",
          }}
        >
          <div className="flying-item-content">🛒</div>
        </div>
      )}
    </div>
  );
};

export default FluidMorphTabs;
